import { isPlatformBrowser } from '@angular/common';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { USER_LEVEL_ENUM } from '@core/types/user-profile.type';
import { LOCAL_STORAGE_KEYS } from '@shared/constants';
import { InjectableApi } from '@shared/decorators';
import { CookieStorageService } from '@shared/services/cookie-storage.service';
import { EMPTY, catchError, tap } from 'rxjs';
import { AuthApiService } from './login-invitation.service';
import { NavigationService } from './navigation.service';
import { StorageService } from './storage-service';
import { LocalStorageKeyType, TOKENS, TOKENS_AUTH } from '../types/token.type';
import { ForceAny } from '../../shared/types';
import { USER_LOGOUT_ENDPOINTS } from '../../shared/constants/shared.const';

@InjectableApi({
  providedIn: 'root',
  baseSegment: 'auth',
})
export class TokenService {
  tokens: TOKENS[] = [...Object.values(TOKENS)];
  isLoggedOut = false;
  private readonly cookiesLoginKey = 'isCookiesLogin';
  private readonly platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private navigationService = inject(NavigationService);
  private authService = inject(AuthApiService);
  private cookieService = inject(CookieStorageService);
  private themeService = inject(StorageService);
  constructor() {}

  /**
   * @function getAuthToken To get a token by name from local storage.
   * @param tokenName The token name by which get the token.
   * @returns The token value.
   */
  getAuthToken(tokenName: TOKENS): string | null {
    if (isPlatformBrowser(this.platformId) && tokenName) {
      return this.readToken(tokenName);
    }
    return null;
  }

  private readToken(tokenName: TOKENS): string | null {
    const tokenFromStorage = localStorage.getItem(tokenName);
    if (tokenFromStorage) return tokenFromStorage;

    const tokenFromCookie = this.getCookieToken(tokenName);
    if (tokenFromCookie) {
      localStorage.setItem(tokenName, tokenFromCookie);
      return tokenFromCookie;
    }

    return null;
  }

  private getCookieToken(tokenName: TOKENS): string | null {
    const cookieNames = [tokenName, tokenName.toLowerCase()];

    for (const cookieName of cookieNames) {
      const token = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${cookieName}=`))
        ?.split('=')[1];

      if (token && token !== 'undefined' && token !== 'null') {
        return decodeURIComponent(token);
      }
    }

    return null;
  }

  /**
   * Setting tokens in local Storage
   * @param headers Http Headers
   */
  setTokens(response: HttpResponse<ForceAny>) {
    const accessToken = response.headers.get('X-Access-Token');
    const refreshToken = response.headers.get('X-Refresh-Token');

    if (accessToken) {
      this.isLoggedOut = false;
      const tokenHeaders = new HttpHeaders({
        'X-Access-Token': accessToken,
        'X-Refresh-Token': refreshToken || '',
      });
      this.tokens.forEach((tokenName) => {
        const tokenValue = tokenHeaders.get(tokenName);
        if (isPlatformBrowser(this.platformId)) {
          if (tokenValue && tokenValue !== 'undefined')
            localStorage.setItem(tokenName, tokenValue);
        }
      });
    }
  }

  /**
   * @function isAuthenticated To check whether user is logged in or not.
   * @returns The user is authenticated or not.
   */
  isAuthenticated(): boolean {
    return this.isCookiesLogin() || !!this.getAuthToken(TOKENS.accessToken);
  }

  setCookiesLogin(isCookiesLogin: boolean): void {
    if (isCookiesLogin) {
      localStorage.setItem(this.cookiesLoginKey, 'true');
      return;
    }

    localStorage.removeItem(this.cookiesLoginKey);
  }

  isCookiesLogin(): boolean {
    return (
      localStorage.getItem(this.cookiesLoginKey) === 'true' &&
      !this.hasTokenLogin()
    );
  }

  hasTokenLogin(): boolean {
    return !!this.readToken(TOKENS.accessToken);
  }

  clearAuthTokens(): void {
    this.tokens.forEach((tokenName) => localStorage.removeItem(tokenName));
  }

  clean() {
    sessionStorage.clear();
    localStorage.clear();
  }

  logoutUser() {
    const isThirdPartyUser =
      this.navigationService.userData?.level ===
      USER_LEVEL_ENUM.THIRD_PARTY_USER;

    const userLogEndpoint = isThirdPartyUser
      ? USER_LOGOUT_ENDPOINTS.CLIENT
      : USER_LOGOUT_ENDPOINTS.USER;

    // this.navigationService.resetNavigationData()
    this.authService
      .logout()
      .pipe(
        tap(() => {
          this.clean();
          this.cookieService.clearAllCookie();
          void this.router.navigate([userLogEndpoint]).then(() => {
            this.storageService.setDefaultTheme();
            this.removeItem('CLIENT_ID');
          });
          this.isLoggedOut = true;
        }),
        catchError((_error) => {
          // Still clean up and redirect even if logout API fails
          this.logoutCred();
          // Return empty observable to complete the stream
          return EMPTY;
        }),
      )
      .subscribe();
  }

  logoutCred() {
    this.themeService.setDefaultTheme();
    this.clean();
    void this.router.navigate(['/auth/login']);
    this.isLoggedOut = true;
  }

  /**
   * @function get2FAKeys To get a token by name from local storage.
   * @param tokenName The token name by which get the token.
   * @returns The token value.
   */
  get2FAKeys(tokenName: TOKENS_AUTH): string | null {
    if (isPlatformBrowser(this.platformId) && tokenName) {
      return localStorage.getItem(tokenName);
    }
    return null;
  }

  /**
   * Set 2FA by name in local storage
   * @param tokenName The 2FA name to be set.
   * @param tokenValue The 2FA value to store.
   */
  set2FAKeys(tokenName: string, tokenValue: string): void {
    if (isPlatformBrowser(this.platformId) && tokenName && tokenValue) {
      localStorage.setItem(tokenName, tokenValue);
    }
  }

  /**
   * Remove 2FA key by name from local storage.
   * @param tokenName The token name to remove.
   */
  remove2FAKeys(tokenName: TOKENS_AUTH): void {
    if (isPlatformBrowser(this.platformId) && tokenName) {
      localStorage.removeItem(tokenName);
    }
  }

  /**
   * Set key by name in local storage
   * @param keyName The key name to be set.
   * @param keyValue The key value to store.
   */
  setItem(keyName: LocalStorageKeyType, keyValue: string): void {
    localStorage.setItem(LOCAL_STORAGE_KEYS[keyName], keyValue);
  }

  /**
   * @function getItem To get a key value by name from local storage.
   * @param keyName The token name by which get the token.
   * @returns The token value.
   */
  getItem(keyName: LocalStorageKeyType): string | null {
    return localStorage.getItem(LOCAL_STORAGE_KEYS[keyName]);
  }

  /**
   * Remove  key by name from local storage.
   * @param keyName The key name to remove.
   */
  removeItem(keyName: LocalStorageKeyType): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS[keyName]);
  }
}
